import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { withStyles, Card, MaterialSymbol, PackageDetails as PackageDetailsData } from 'vopay';

import AgreementDetails, { AgreementDetailsHandle } from './tabs/AgreementDetails/AgreementDetails';
import AccountConfiguration, { AccountConfigurationHandle } from './tabs/AccountConfiguration/AccountConfiguration';
import AccountSettings, { AccountSettingsHandle } from './tabs/AccountSettings/AccountSettings';
import PaymentRails, { PaymentRailsHandle } from './tabs/PaymentRails/PaymentRails';
import AdditionalServices, { AdditionalServicesHandle } from './tabs/AdditionalServices/AdditionalServices';

import styles from './PackageDetails.module.scss';

const cn = withStyles(styles);

// Account Configuration settings keys the backend returns that should be optional.
const OPTIONAL_ACCOUNT_CONFIG_FIELDS = ['DataAggregator', 'SystemOfRecord'];

const TABS = [
  'agreementDetails',
  'accountConfiguration',
  'accountSettings',
  'paymentRails',
  'additionalServices',
] as const;
type Tab = (typeof TABS)[number];

export type TabValues = Record<string, string | boolean>;
export type TabValuesMap = Partial<Record<Tab, TabValues>>;

type PackageDetailsProps = {
  details: PackageDetailsData;
  country: string;
  onValidChange: (isValid: boolean) => void;
  onValuesChange: (values: TabValuesMap) => void;
  onReviewChange: (remainingSections: string[]) => void;
  onErrorChange: (errorSections: string[]) => void;
};

const PackageDetails: FunctionComponent<PackageDetailsProps> = ({
  details,
  country,
  onValidChange,
  onValuesChange,
  onReviewChange,
  onErrorChange,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'R1AccountCreation.Billing.PackageDetails.Tabs' });
  const [currentTab, setCurrentTab] = useState<Tab>('agreementDetails');
  const [visitedTabs, setVisitedTabs] = useState<Set<Tab>>(new Set<Tab>());
  const [tabValidity, setTabValidity] = useState<Partial<Record<Tab, boolean>>>({});
  const tabValidityRef = useRef<Partial<Record<Tab, boolean>>>({});
  const tabValuesRef = useRef<TabValuesMap>({});

  // A section counts as reviewed once it's been the active tab; the current one always counts.
  const reviewedTabs = useMemo(() => new Set<Tab>([...visitedTabs, currentTab]), [visitedTabs, currentTab]);

  useEffect(() => {
    onReviewChange(TABS.filter((tab) => !reviewedTabs.has(tab)).map((tab) => t(tab)));
  }, [reviewedTabs, onReviewChange, t]);

  useEffect(() => {
    onErrorChange(TABS.filter((tab) => tabValidity[tab] === false).map((tab) => t(tab)));
  }, [tabValidity, onErrorChange, t]);

  const defaultCurrency = details.BillingSettings?.Currency.value ?? 'CAD';
  const currencySettingsKey = useMemo(() => {
    const entry = Object.entries(details.AgreementDetails ?? {}).find(([key]) => key === 'Currency');
    return entry?.[1].settings;
  }, [details.AgreementDetails]);
  const [currency, setCurrency] = useState(defaultCurrency);

  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [currentTab]);

  const agreementDetailsRef = useRef<AgreementDetailsHandle>(null);
  const accountConfigurationRef = useRef<AccountConfigurationHandle>(null);
  const accountSettingsRef = useRef<AccountSettingsHandle>(null);
  const paymentRailsRef = useRef<PaymentRailsHandle>(null);
  const additionalServicesRef = useRef<AdditionalServicesHandle>(null);

  const tabRefs: Record<Tab, React.RefObject<{ touchAllFields: () => void } | null>> = {
    agreementDetails: agreementDetailsRef,
    accountConfiguration: accountConfigurationRef,
    accountSettings: accountSettingsRef,
    paymentRails: paymentRailsRef,
    additionalServices: additionalServicesRef,
  };

  const handleTabChange = (tab: Tab) => {
    if (tab === currentTab) return;
    tabRefs[currentTab].current?.touchAllFields();
    setVisitedTabs((prev) => new Set([...prev, currentTab]));
    setCurrentTab(tab);
  };

  const handleTabValidChange = (tab: Tab, isValid: boolean) => {
    tabValidityRef.current = { ...tabValidityRef.current, [tab]: isValid };
    setTabValidity(tabValidityRef.current);
    onValidChange(TABS.every((t) => tabValidityRef.current[t] === true));
  };

  const handleTabValuesChange = (tab: Tab, values: Record<string, string | boolean>) => {
    tabValuesRef.current = { ...tabValuesRef.current, [tab]: values };
    onValuesChange(tabValuesRef.current);
    if (tab === 'agreementDetails' && currencySettingsKey && typeof values[currencySettingsKey] === 'string') {
      setCurrency(values[currencySettingsKey] as typeof defaultCurrency);
    }
  };

  const tabHasError = (tab: Tab) => visitedTabs.has(tab) && tabValidity[tab] === false;

  return (
    <Card className={cn('container')} hoverEffect={false}>
      <nav className={cn('breadcrumb')}>
        {TABS.map((tab, idx) => (
          <span key={tab} className={cn('breadcrumbItem')}>
            {idx > 0 && (
              <span className={cn('separator')}>
                <MaterialSymbol icon="chevron_right" size="24px" color="#2D2D2D80" family="rounded" />
              </span>
            )}
            <button
              type="button"
              data-testid={`tab-${tab}`}
              data-error={tabHasError(tab) || undefined}
              onClick={() => handleTabChange(tab)}
              className={cn('breadcrumbBtn', currentTab === tab && 'active', tabHasError(tab) && 'error')}
            >
              {!reviewedTabs.has(tab) && <span className={cn('reviewDot')} data-testid={`reviewDot-${tab}`} />}
              {t(tab)}
            </button>
          </span>
        ))}
      </nav>

      <div className={cn('content')} ref={contentRef}>
        <div className={cn(currentTab !== 'agreementDetails' && 'hidden')}>
          <AgreementDetails
            ref={agreementDetailsRef}
            data={details.AgreementDetails}
            currency={currency}
            onValidChange={(v) => handleTabValidChange('agreementDetails', v)}
            onValuesChange={(v) => handleTabValuesChange('agreementDetails', v)}
          />
        </div>
        <div className={cn(currentTab !== 'accountConfiguration' && 'hidden')}>
          <AccountConfiguration
            ref={accountConfigurationRef}
            data={details.AccountConfiguration}
            optionalFields={OPTIONAL_ACCOUNT_CONFIG_FIELDS}
            currency={currency}
            onValidChange={(v) => handleTabValidChange('accountConfiguration', v)}
            onValuesChange={(v) => handleTabValuesChange('accountConfiguration', v)}
          />
        </div>
        <div className={cn(currentTab !== 'accountSettings' && 'hidden')}>
          <AccountSettings
            ref={accountSettingsRef}
            data={details.AccountSettings}
            currency={currency}
            country={country}
            planType={details.BillingSettings?.PlanType.value ?? 'subscription'}
            useProgressivePricing={details.BillingSettings?.UseProgressivePricing?.value ?? false}
            onValidChange={(v) => handleTabValidChange('accountSettings', v)}
            onValuesChange={(v) => handleTabValuesChange('accountSettings', v)}
          />
        </div>
        <div className={cn(currentTab !== 'paymentRails' && 'hidden')}>
          <PaymentRails
            ref={paymentRailsRef}
            data={details.PaymentRails}
            currency={currency}
            country={country}
            planType={details.BillingSettings?.PlanType.value ?? 'subscription'}
            useProgressivePricing={details.BillingSettings?.UseProgressivePricing?.value ?? false}
            onValidChange={(v) => handleTabValidChange('paymentRails', v)}
            onValuesChange={(v) => handleTabValuesChange('paymentRails', v)}
          />
        </div>
        <div className={cn(currentTab !== 'additionalServices' && 'hidden')}>
          <AdditionalServices
            ref={additionalServicesRef}
            data={details.AdditionalServices}
            currency={currency}
            country={country}
            planType={details.BillingSettings?.PlanType.value ?? 'subscription'}
            useProgressivePricing={details.BillingSettings?.UseProgressivePricing?.value ?? false}
            onValidChange={(v) => handleTabValidChange('additionalServices', v)}
            onValuesChange={(v) => handleTabValuesChange('additionalServices', v)}
          />
        </div>
      </div>
    </Card>
  );
};

export default PackageDetails;
